import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar,
  faDownload,
  faBox,
  faUsers,
  faMoneyBillWave,
  faTruck,
  faChartLine,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../../services/userService';
import { shipmentService } from '../../../services/shipmentService';
import { getSubscriptionStats } from '../../../services/subscriptionService';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalShipments: 0,
    totalSubscriptions: 0,
    deliveredCount: 0,
    pendingCount: 0,
    inTransitCount: 0,
    rejectedCount: 0,
    pendingRevenue: 0,
    revenue: {
      total: 0,
      today: 0,
      transactionCount: 0
    }
  });

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const [userStatsResult, shipmentStatsResult, subscriptionStatsResult] = await Promise.allSettled([
          userService.getStats(),
          shipmentService.getStats(token),
          getSubscriptionStats()
        ]);

        let nextStats = {
          totalUsers: 0,
          totalShipments: 0,
          totalSubscriptions: 0,
          deliveredCount: 0,
          pendingCount: 0,
          inTransitCount: 0,
          rejectedCount: 0,
          pendingRevenue: 0,
          revenue: {
            total: 0,
            today: 0,
            transactionCount: 0
          }
        };

        if (userStatsResult.status === 'fulfilled') {
          nextStats = { ...nextStats, ...userStatsResult.value };
        }

        if (shipmentStatsResult.status === 'fulfilled') {
          const s = shipmentStatsResult.value;
          nextStats = {
            ...nextStats,
            totalShipments: s.totalShipments || 0,
            deliveredCount: s.deliveredCount || 0,
            pendingCount: s.pendingCount || 0,
            inTransitCount: s.inTransitCount || 0,
            rejectedCount: s.rejectedCount || 0,
            pendingRevenue: (nextStats.pendingRevenue || 0) + (s.pendingRevenue || 0),
            revenue: {
              total: (nextStats.revenue?.total || 0) + (s.totalRevenue || 0),
              today: (nextStats.revenue?.today || 0) + (s.todayRevenue || 0),
              transactionCount: (nextStats.revenue?.transactionCount || 0) + (s.deliveredCount || 0)
            }
          };
        }

        if (subscriptionStatsResult.status === 'fulfilled') {
          const sub = subscriptionStatsResult.value;
          nextStats = {
            ...nextStats,
            totalSubscriptions: sub.totalActive || 0,
            pendingRevenue: (nextStats.pendingRevenue || 0) + (sub.pendingRevenue || 0),
            revenue: {
              total: (nextStats.revenue?.total || 0) + (sub.totalRevenue || 0),
              today: (nextStats.revenue?.today || 0) + (sub.todayRevenue || 0),
              transactionCount: (nextStats.revenue?.transactionCount || 0) + (sub.totalActive || 0)
            }
          };
        }

        setStats(nextStats);

        if (userStatsResult.status === 'rejected' && shipmentStatsResult.status === 'rejected') {
          setError('Failed to fetch latest statistics. Some data may be outdated.');
        }
      } catch (e) {
        console.error('Failed to fetch stats:', e);
        setError('Failed to fetch latest statistics. Some data may be outdated.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleExport = () => {
    const dataStr = JSON.stringify(stats, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `fex-logistics-report-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Reports</h2>
          <p className="text-gray-500 text-sm">Comprehensive overview of system performance and metrics</p>
        </div>
        <button
          onClick={handleExport}
          disabled={loading}
          className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faDownload} className="mr-2" />
          Export Report
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3" />
          {error}
        </div>
      )}

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <FontAwesomeIcon icon={faUsers} className="text-xl" />
            </div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Total Users</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <FontAwesomeIcon icon={faBox} className="text-xl" />
            </div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Total Shipments</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalShipments}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-xl" />
            </div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Total Revenue (Shipment History)</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue.total)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-50 text-orange-600 rounded-xl">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-xl" />
            </div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Pending Shipment Revenue</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.pendingRevenue)}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-50 text-orange-600 rounded-xl">
              <FontAwesomeIcon icon={faChartLine} className="text-xl" />
            </div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Today's Revenue</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue.today)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shipment Breakdown */}
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            <FontAwesomeIcon icon={faTruck} className="mr-2 text-red-500" />
            Shipment Status Breakdown
          </h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Delivered</span>
                <span className="text-sm font-bold text-gray-900">{stats.deliveredCount}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div 
                  className="bg-green-500 h-2.5 rounded-full"
                  style={{ width: `${stats.totalShipments > 0 ? (stats.deliveredCount / stats.totalShipments) * 100 : 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">In Transit</span>
                <span className="text-sm font-bold text-gray-900">{stats.inTransitCount}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div 
                  className="bg-blue-500 h-2.5 rounded-full"
                  style={{ width: `${stats.totalShipments > 0 ? (stats.inTransitCount / stats.totalShipments) * 100 : 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Pending</span>
                <span className="text-sm font-bold text-gray-900">{stats.pendingCount}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div 
                  className="bg-yellow-500 h-2.5 rounded-full"
                  style={{ width: `${stats.totalShipments > 0 ? (stats.pendingCount / stats.totalShipments) * 100 : 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Rejected/Cancelled</span>
                <span className="text-sm font-bold text-gray-900">{stats.rejectedCount}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div 
                  className="bg-red-500 h-2.5 rounded-full"
                  style={{ width: `${stats.totalShipments > 0 ? (stats.rejectedCount / stats.totalShipments) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Summary */}
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            <FontAwesomeIcon icon={faChartBar} className="mr-2 text-indigo-500" />
            Business Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Paid Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.revenue.transactionCount}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Avg. Transaction Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.revenue.transactionCount > 0 ? stats.revenue.total / stats.revenue.transactionCount : 0)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalShipments > 0 ? ((stats.deliveredCount / stats.totalShipments) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">User Growth</p>
              <p className="text-2xl font-bold text-gray-900">Stable</p>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-indigo-50 text-indigo-800 rounded-2xl text-sm border border-indigo-100">
            <p className="flex items-center font-medium">
              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
              Revenue is calculated from shipment and subscription history in your database.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
